import React, {
  useState, useEffect, useRef,
  useCallback,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Button, Heading, Icon, LinkAsButton, TextInput,
} from '@printdeal/ui-library';

import { ServiceFactory } from '../../../../../services/ServiceFactory';

const STATES = {
  INITIAL: 'initial',
  IDLE: 'idle',
  EDITING: 'editing',
};

const ESC_KEY = 'Escape';

interface EditDesignNameProps {
  initialDesignName: string,
  id: string,
  updateDesign: (updatedDesign: { id: string, deleted: boolean }) => void,
}

const designService = ServiceFactory.createDesignService();

const EditDesignName = ({ initialDesignName, id, updateDesign }:EditDesignNameProps) => {
  const [state, setState] = useState(STATES.INITIAL);
  const [designName, setDesignName] = useState(initialDesignName);
  const intl = useIntl();
  const toggleEditDesignNameButton = useRef<HTMLButtonElement>(null);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDesignName(event.target.value);
  };
  const editDesignName = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    designService.editDesign({ name: designName, id });
    updateDesign({ id, deleted: false });
    setState(STATES.IDLE);
  };

  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    if (event.code === ESC_KEY) {
      setDesignName(initialDesignName);
      setState(STATES.IDLE);
    }
  }, [initialDesignName]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  useEffect(() => {
    if (state === STATES.IDLE) {
      toggleEditDesignNameButton?.current?.focus();
    }
  }, [toggleEditDesignNameButton, state]);

  return (
    <>
      { state === STATES.EDITING && (
        <form onSubmit={editDesignName} method="POST" onBlur={editDesignName}>
          <div className="pd-flex pd-w-full pd-gap-3">
            <TextInput
              name="designNameInput"
              className="pd-mb-3 pd-w-full"
              value={designName}
              onChange={onChange}
              autoFocus
            />
            <Button
              type="submit"
              variant="secondary"
              iconAfter={<Icon icon="save" />}
            />
          </div>
        </form>
      )}

      {(state === STATES.IDLE || state === STATES.INITIAL) && (
        <>
          <Heading as="h2" variant="display" className="pd-mb-2">
            {designName || <FormattedMessage id="account.designList.no.designName" defaultMessage="Nameless" />}
          </Heading>
          <LinkAsButton
            iconBefore="edit"
            variant="secondary"
            className="pd-text-left"
            onClick={() => setState(STATES.EDITING)}
            screenReaderLabel={intl.formatMessage({ id: 'account.designList.edit.designName', defaultMessage: 'Edit design name' })}
            ref={toggleEditDesignNameButton}
          >
            <FormattedMessage id="account.designList.edit.designName" defaultMessage="Edit design name" />
          </LinkAsButton>
        </>
      )}
    </>
  );
};

export default EditDesignName;
